import { apiUrl } from "config/envConfig";
import http from "./httpService";
import { getToken } from "./tokenUtils";
import { OrderTypeEnum } from "enums/shared";
import {
  DEFAULT_PAGE,
  DEFAULT_ROWS_PER_PAGE,
} from "constants/paginationConstants";

const path = "/order-mgmt/admin";

const url = apiUrl;

// const api = process.env .name of the api

/**
 * Get all orders.
 *
 * @param {number} pageSize - Number of rows per page.
 * @param {number} pageNumber - Current page number.
 * @param {number[] | null} status - Array of status numbers.
 * @param {string | null} senderName - Sender's name filter.
 * @param {string | null} orderNumber - Order number filter.
 * @param {string | null} receiverName - Receiver's name filter.
 * @param {string | null} driverName - Driver's name filter.
 * @param {boolean} sortDescending - Whether to sort in descending order.
 * @param {string | null} from - Start date filter (ISO format).
 * @param {string | null} to - End date filter (ISO format).
 * @param {string | null} pickupDate - Pickup date filter (ISO format).
 * @param {string | null} dropoffDate - Dropoff date filter (ISO format).
 * @param {string | null} creationDate - Creation date filter (ISO format).
 * @param {string} language - Language for the response (e.g., 'en', 'ar').
 * @param {string | null} senderId - ID of the sender.
 * @param {number | null} orderType - Order type filter (0 for Regular, 1 for Expedited).
 * @returns {Promise<Object>} Filtered order list.
 */
export const getOrders = async (
  pageSize = DEFAULT_ROWS_PER_PAGE,
  pageNumber = DEFAULT_PAGE,
  status = null,
  senderName = null,
  orderNumber = null,
  receiverName = null,
  driverName = null,
  pickUpLocation = null,
  dropOffLocation = null,
  sortDescending = true,
  from = null,
  to = null,
  pickupDate = null,
  dropoffDate = null,
  creationDate = null,
  language = "ar",
  senderId = null,
  orderType = null
) => {
  const body = {
    pageSize,
    pageNumber,
    sortDescending,
  };

  if (status && status.length > 0) {
    body.status = status.map(Number);
  }

  if (from) {
    body.from = new Date(from).toISOString();
  }

  if (to) {
    body.to = new Date(to).toISOString();
  }

  if (pickupDate) {
    body.pickupDate = new Date(pickupDate).toISOString();
  }

  if (dropoffDate) {
    body.dropoffDate = new Date(dropoffDate).toISOString();
  }

  if (creationDate) {
    body.creationDate = new Date(creationDate).toISOString();
  }

  if (senderId) {
    body.senderId = senderId;
  }

  if (senderName) {
    body.senderName = senderName;
  }

  if (orderNumber) {
    body.orderNumber = orderNumber;
  }

  if (receiverName) {
    body.receiverName = receiverName;
  }

  if (driverName) {
    body.driverName = driverName;
  }

  if (pickUpLocation) {
    body.pickUpLocation = pickUpLocation;
  }

  if (dropOffLocation) {
    body.dropOffLocation = dropOffLocation;
  }

  if (orderType !== null && orderType !== undefined) {
    // Backend expects expedited orders to be represented by 2, regular by 1
    if (orderType === OrderTypeEnum.expedited) {
      body.orderType = 2;
    } else {
      body.orderType = 1;
    }
  }

  const {
    data: { data },
  } = await http.post(`${url}${path}/orders`, body, {
    headers: {
      Lang: language,
    },
  });

  return data;
};

/**
 * Cancel order.
 * @param {string} orderId Order id.
 * @returns {Promise<Object>} Cancellation confirmation.
 */
export const cancelOrder = async (orderId) => {
  const {
    data: { data },
  } = await http.patch(`${url}${path}/order/cancel?orderId=${orderId}`);
  return data;
};

/**
 * Get order by ID.
 * @param {string} orderId Order ID.
 * @param {string} language Language code (default: "ar").
 * @returns {Promise<Object>} Order details.
 */
export const getOrderById = async (orderId, language = "ar") => {
  //here lat and lon exisit for each order

  const {
    data: { data },
  } = await http.get(`${url}${path}/order?orderId=${orderId}`);

  return data;
};

/**
 * Get sender (business) details by ID. second tap
 *
 * @param {string} senderId Sender ID.
 * @return {Promise<Object>} Sender details.
 */
export const getSenderById = async (senderId) => {
  const {
    data: { data },
  } = await http.get(`${url}/user-mgmt/admin/sender?senderId=${senderId}`);

  return data;
};

/**
 * Get driver details by ID (third tab).
 *
 * @param {string} driverId Driver ID.
 * @return {Promise<Object>} Driver details.
 */
export const getDriverInfo = async (driverId) => {
  const token = getToken();
  const {
    data: { data },
  } = await http.get(`${url}/user-mgmt/admin/driver?driverId=${driverId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return data;
};

/**
 * Get receiver details by ID.
 *
 * @param {string} receiverId Receiver ID.
 * @return {Promise<Object>} Receiver details.
 */
export const getReceiverInfo = async (receiverId) => {
  const {
    data: { data },
  } = await http.get(
    `${url}/user-mgmt/admin/receiver?receiverId=${receiverId}`,
    {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    }
  );
  return data;
};

/**
 * Get order logs.
 *
 * @param {string} orderId Order ID.
 * @returns {Promise<Object>} Order logs.
 */
export const getOrderLogs = async (orderId) => {
  const {
    data: { data },
  } = await http.get(`${url}/order-mgmt/admin/logs?orderId=${orderId}`);
  return { history: data.history, issues: data.issues };
};

/**
 * Get driver locations based on coordinates.
 *
 * @param {number} latitude Latitude coordinate.
 * @param {number} longitude Longitude coordinate.
 * @param {number} pageSize Number of results per page.
 * @param {number} pageNumber Page number.
 * @param {number} [orderType=1] Order type filter (1 for Regular, 2 for Expedited).
 * @returns {Promise<Object>} List of driver locations.
 */
export const getDriverLocations = async (
  pageSize,
  pageNumber,
  latitude,
  longitude,
  orderType = 1,
  isActive = true
) => {
  const params = new URLSearchParams({
    PageSize: pageSize,
    PageNumber: pageNumber,
    "Coordinate.Latitude": latitude,
    "Coordinate.Longitude": longitude,
    OrderType: orderType,
  });

  // include isActive flag if provided
  if (typeof isActive === "boolean") {
    params.append("IsActive", isActive);
  }

  const { data } = await http.get(`${url}/location-mgmt/admin/driver/locations?${params.toString()}`);
  return data;
};

/**
 * Assign driver to an order.
 *
 * @param {string} orderId Order ID.
 * @param {string} driverId Driver ID.
 * @returns {Promise<Object>} API response.
 */
export const assignDriver = async (orderId, driverId) => {
  const {
    data: { data },
  } = await http.patch(
    `${url}/order-mgmt/admin/order/assigndriver?orderId=${orderId}&driverId=${driverId}`
  );
  return data;
};

//action for pending pickup
export const startPickupTrip = async (orderId, driverId) => {
  try {
    const response = await http.patch(
      `${url}/order-mgmt/admin/order/startpickup?orderId=${orderId}&driverId=${driverId}`
    );
    return response.data;
  } catch (error) {
    throw new Error("Failed to start pickup trip");
  }
};

//action for pickupstarted
export const arriveAtPickup = async (orderId, driverId) => {
  try {
    const response = await http.patch(
      `${url}/order-mgmt/admin/order/arriveatpickup?orderId=${orderId}&driverId=${driverId}`
    );
    return response.data;
  } catch (error) {
    throw new Error("Failed to arrive at pickup location");
  }
};

//action for pickupstarted
export const pickuporder = async (orderId, driverId) => {
  try {
    const response = await http.patch(
      `${url}/order-mgmt/admin/order/pickuporder?orderId=${orderId}&driverId=${driverId}`
    );
    return response.data;
  } catch (error) {
    throw new Error("Failed to arrive at pickup location");
  }
};

//action for reschedlue
export const StartOrderDelivery = async (orderId, driverId) => {
  try {
    const response = await http.patch(
      `${url}/order-mgmt/admin/order/startdelivery?orderId=${orderId}&driverId=${driverId}`
    );
    return response.data;
  } catch (error) {
    throw new Error("Failed to arrive at pickup location");
  }
};

//action for pickUpStarted  will be Arrived At Pickup => collected

export const arriveatdropof = async (orderId, driverId) => {
  try {
    const response = await http.patch(
      `${url}/order-mgmt/admin/order/arriveatdropoff?orderId=${orderId}&driverId=${driverId}`
    );
    return response.data;
  } catch (error) {
    throw new Error("Failed to arrive at pickup location");
  }
};

//action for arrivedAtDropOff
export const finishorderdelivery = async (orderId, driverId) => {
  try {
    const response = await http.patch(
      `${url}/order-mgmt/admin/order/finishdelivery?orderId=${orderId}&driverId=${driverId}`
    );
    return response.data;
  } catch (error) {
    throw new Error("Failed to arrive at pickup location");
  }
};

export const getSenderOrders = async (
  senderId,
  pageNumber = DEFAULT_PAGE,
  pageSize = DEFAULT_ROWS_PER_PAGE,
  status = null,
  orderNumber = null,
  receiverName = null,
  driverName = null,
  sortDescending = true,
  sortBy = null,
  pickupDate = null,
  dropoffDate = null
) => {
  const body = {
    pageSize,
    pageNumber,
    sortDescending,
    ...(status?.length > 0 && { status: status.map(Number) }),
    ...(orderNumber && { orderNumber }),
    ...(receiverName && { receiverName }),
    ...(driverName && { driverName }),
    ...(sortBy && { sortBy }),
    ...(pickupDate && { pickupDate: new Date(pickupDate).toISOString() }),
    ...(dropoffDate && { dropoffDate: new Date(dropoffDate).toISOString() }),
  };

  const { data: response } = await http.post(
    `${url}${path}/sender/${senderId}/orders`,
    body
  );

  return response.data;
};

/**
 * Rejects an order with a specific reason.
 * @param {string} orderId The ID of the order.
 * @param {string} driverId The ID of the driver assigned to the order.
 * @param {string} rejectReason The reason for rejection.
 * @returns {Promise<any>} API response.
 */
export const rejectOrder = async (orderId, driverId, rejectReason) => {
  try {
    const endpoint = `${url}/order-mgmt/admin/order/reject?orderId=${orderId}&driverId=${driverId}&rejectReason=${rejectReason}`;

    const { data } = await http.patch(endpoint);
    return data;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || "Failed to reject the order.";
    throw new Error(errorMessage);
  }
};

/**
 * Fails an order delivery with a specific reason.
 * @param {string} orderId The ID of the order.
 * @param {string} driverId The ID of the driver assigned to the order.
 * @param {string} failureReason The reason for the delivery failure.
 * @returns {Promise<any>} API response.
 */
export const failOrderDelivery = async (orderId, driverId, failureReason) => {
  try {
    const endpoint = `${url}/order-mgmt/admin/order/failed?orderId=${orderId}&driverId=${driverId}&failuerReason=${failureReason}`;

    const { data } = await http.patch(endpoint);
    return data;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || "Failed to report delivery failure.";
    throw new Error(errorMessage);
  }
};

/**
 * Delays (reschedules) an order to a new time slot.
 * @param {string} orderId The ID of the order.
 * @param {string} driverId The ID of the driver.
 * @param {{start: string, end: string}} payload The start and end time for the new slot in ISO format.
 * @returns {Promise<any>} API response.
 */
export const delayOrder = async (orderId, driverId, payload) => {
  try {
    // Endpoint from the provided curl command
    const endpoint = `${url}/order-mgmt/admin/order/reschadual?orderId=${orderId}&driverId=${driverId}`;

    const { data } = await http.patch(endpoint, payload);
    return data;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || "Failed to delay the order.";
    throw new Error(errorMessage);
  }
};

/**
 * Submits a request to change the driver for an order.
 * @param {object} params - The request parameters.
 * @param {string} params.orderId - The ID of the order.
 * @param {number} params.reason - The numerical reason for the change.
 * @param {string} params.explanation - The text explanation.
 * @param {File[]} params.files - An array of files to attach.
 * @returns {Promise<any>} API response.
 */
export const requestDriverChange = async ({
  orderId,
  reason,
  explanation,
  files,
}) => {
  try {
    const formData = new FormData();
    formData.append("OrderId", orderId);
    formData.append("Reason", reason);
    formData.append("Explanation", explanation);

    // Append each file to the form data
    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append("Attachment", file);
      });
    }

    const endpoint = `${url}/order-mgmt/admin/order/requestdriverchange`;

    const { data } = await http.post(endpoint, formData);
    return data;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      "Failed to submit driver change request.";
    throw new Error(errorMessage);
  }
};
